import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, BellOff, Check, CheckCheck, Trash2, Settings, 
  Building2, Star, DollarSign, Gift, Target, Shield,
  ChevronRight, Loader2, Mail, MailOpen, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Switch } from '@/components/ui/switch';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush 
} from '@/services/pushNotifications';

const API = `${window.location.origin}/api`;

const NOTIFICATION_ICONS = {
  business_verified: { icon: Building2, color: 'text-green-400', bg: 'bg-green-500/10' },
  business_rejected: { icon: Building2, color: 'text-red-400', bg: 'bg-red-500/10' },
  review_received: { icon: Star, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  mel_earned: { icon: DollarSign, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  bounty_claimed: { icon: Target, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  bounty_completed: { icon: Target, color: 'text-green-400', bg: 'bg-green-500/10' },
  claim_approved: { icon: Shield, color: 'text-green-400', bg: 'bg-green-500/10' },
  claim_rejected: { icon: Shield, color: 'text-red-400', bg: 'bg-red-500/10' },
  withdrawal_processed: { icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10' },
  referral_signup: { icon: Gift, color: 'text-pink-400', bg: 'bg-pink-500/10' },
  welcome: { icon: Bell, color: 'text-blue-400', bg: 'bg-blue-500/10' },
};

function NotificationItem({ notification, onMarkRead, onDelete }) {
  const iconConfig = NOTIFICATION_ICONS[notification.type] || { icon: Bell, color: 'text-zinc-400', bg: 'bg-zinc-500/10' };
  const Icon = iconConfig.icon;
  
  const getLink = () => {
    const data = notification.data || {};
    if (data.business_id) return `/businesses/${data.business_id}`;
    if (data.bounty_id) return `/bounties/${data.bounty_id}`;
    if (notification.type === 'mel_earned' || notification.type === 'withdrawal_processed') return '/dashboard?tab=wallet';
    return null;
  };
  
  const link = getLink();
  const timeAgo = getTimeAgo(notification.created_at);
  
  const content = (
    <div className={`flex items-start gap-4 p-4 rounded-xl transition-colors ${
      notification.is_read 
        ? 'bg-zinc-900/30' 
        : 'bg-zinc-900/70 border border-zinc-800'
    }`}>
      <div className={`w-10 h-10 rounded-lg ${iconConfig.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${iconConfig.color}`} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className={`font-medium ${notification.is_read ? 'text-zinc-400' : 'text-white'}`}>
            {notification.title}
          </h4>
          {!notification.is_read && (
            <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2" />
          )}
        </div>
        <p className="text-zinc-500 text-sm mt-1 line-clamp-2">{notification.message}</p>
        <div className="flex items-center gap-4 mt-2">
          <span className="text-zinc-600 text-xs">{timeAgo}</span>
          {notification.email_sent && (
            <span className="flex items-center gap-1 text-zinc-600 text-xs">
              <Mail className="w-3 h-3" />
              Email sent
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-1 shrink-0">
        {!notification.is_read && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkRead(notification.notification_id); }}
            className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
            title="Mark as read"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(notification.notification_id); }}
          className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {link && <ChevronRight className="w-4 h-4 text-zinc-600" />}
      </div>
    </div>
  );
  
  if (link) {
    return (
      <Link to={link} className="block hover:opacity-90 transition-opacity">
        {content}
      </Link>
    );
  }
  
  return content;
}

function getTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [enablingPush, setEnablingPush] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
    checkPushStatus();
  }, [activeTab]);

  const checkPushStatus = async () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    
    if (supported) {
      const permission = getNotificationPermission();
      setPushPermission(permission);
      setPushEnabled(permission === 'granted');
    }
  };

  const handlePushToggle = async (enabled) => {
    setEnablingPush(true);
    
    try {
      if (enabled) {
        const permission = await requestNotificationPermission();
        setPushPermission(permission);
        
        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          await subscribeToPush(registration);
          setPushEnabled(true);
          toast.success('Push notifications enabled!');
        } else if (permission === 'denied') {
          toast.error('Notifications blocked. Please enable in browser settings.');
        }
      } else {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      toast.error('Failed to update push notification settings');
    } finally {
      setEnablingPush(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab === 'unread') params.append('unread_only', 'true');
      
      const response = await fetch(`${API}/notifications?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.status === 401) {
        navigate('/');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API}/notifications/preferences`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        setPreferences(await response.json());
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const notif = notifications.find(n => n.notification_id === notificationId);
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
      if (!notif?.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const clearAllNotifications = async () => {
    if (!confirm('Are you sure you want to delete all notifications?')) return;
    
    try {
      await fetch(`${API}/notifications`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const updatePreference = async (key, value) => {
    setSavingPrefs(true);
    try {
      const response = await fetch(`${API}/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });
      
      if (response.ok) {
        setPreferences({ ...preferences, [key]: value });
        toast.success('Preference updated');
      }
    } catch (error) {
      toast.error('Failed to update preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  const preferenceItems = [
    { key: 'email_business_verified', label: 'Business Verified', description: 'When your business listing is approved' },
    { key: 'email_business_rejected', label: 'Business Rejected', description: 'When your business listing is not approved' },
    { key: 'email_review_received', label: 'New Reviews', description: 'When someone reviews your business' },
    { key: 'email_mel_earned', label: 'MEL Earned', description: 'When you earn MEL rewards (can be frequent)' },
    { key: 'email_bounty_updates', label: 'Bounty Updates', description: 'When bounties you created or claimed have updates' },
    { key: 'email_claim_updates', label: 'Claim Updates', description: 'When your business claims are approved or denied' },
    { key: 'email_withdrawal_updates', label: 'Withdrawal Updates', description: 'When your MEL withdrawals are processed' },
    { key: 'email_weekly_digest', label: 'Weekly Digest', description: 'Weekly summary of activity on your businesses' },
    { key: 'email_marketing', label: 'Marketing & Tips', description: 'Tips, features, and promotional content' },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Notifications
              </p>
              <h1 className="text-3xl font-bold text-white tracking-tight">
                Notification Center
              </h1>
            </div>
            
            {unreadCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">
                  {unreadCount} unread
                </span>
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark all read
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'all', label: 'All Notifications' },
              { id: 'unread', label: 'Unread' },
              { id: 'settings', label: 'Email Settings', icon: Settings },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white'
                }`}
              >
                {tab.icon && <tab.icon className="w-4 h-4" />}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 'settings' ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Push Notifications Section */}
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Push Notifications</h2>
                    <p className="text-zinc-400 text-sm">Get notified even when the app is closed</p>
                  </div>
                </div>

                {pushSupported ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                      <div>
                        <p className="text-white font-medium">Enable Push Notifications</p>
                        <p className="text-zinc-500 text-sm">
                          {pushPermission === 'denied' 
                            ? 'Blocked in browser settings' 
                            : 'Receive instant notifications on your device'}
                        </p>
                      </div>
                      <Switch
                        checked={pushEnabled}
                        onCheckedChange={handlePushToggle}
                        disabled={enablingPush || pushPermission === 'denied'}
                      />
                    </div>

                    {pushPermission === 'denied' && (
                      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-red-400 text-sm">
                          Push notifications are blocked. To enable them, click the lock icon in your browser&apos;s address bar and allow notifications.
                        </p>
                      </div>
                    )}

                    {pushEnabled && (
                      <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                        <p className="text-green-400 text-sm flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Push notifications are active. You&apos;ll receive alerts for important updates.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                    <p className="text-zinc-400 text-sm">
                      Push notifications are not supported in this browser. Try using Chrome, Firefox, or Safari.
                    </p>
                  </div>
                )}
              </div>

              {/* Email Notifications Section */}
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Email Notifications</h2>
                    <p className="text-zinc-400 text-sm">Choose what emails you want to receive</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {preferenceItems.map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                      <div>
                        <p className="text-white font-medium">{item.label}</p>
                        <p className="text-zinc-500 text-sm">{item.description}</p>
                      </div>
                      <Switch
                        checked={preferences?.[item.key] ?? true}
                        onCheckedChange={(checked) => updatePreference(item.key, checked)}
                        disabled={savingPrefs}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 rounded-lg bg-zinc-900/30 border border-zinc-800">
                  <p className="text-zinc-400 text-sm">
                    <strong className="text-white">Note:</strong> You&apos;ll always receive in-app notifications regardless of email settings. 
                    Email notifications require a valid Resend API key configured by the admin.
                  </p>
                </div>
              </div>
            </motion.div>
          ) : loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          ) : notifications.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <AnimatePresence>
                {notifications.map((notification) => (
                  <motion.div
                    key={notification.notification_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <NotificationItem
                      notification={notification}
                      onMarkRead={markAsRead}
                      onDelete={deleteNotification}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>

              {notifications.length > 0 && (
                <div className="pt-4 text-center">
                  <button
                    onClick={clearAllNotifications}
                    className="text-zinc-500 hover:text-red-400 text-sm transition-colors"
                  >
                    Clear all notifications
                  </button>
                </div>
              )}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mx-auto mb-4">
                <BellOff className="w-8 h-8 text-zinc-600" />
              </div>
              <h3 className="text-white font-medium mb-2">No notifications</h3>
              <p className="text-zinc-500 text-sm">
                {activeTab === 'unread' 
                  ? "You're all caught up!" 
                  : "You don't have any notifications yet"}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
